import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLanguage } from '@anytag/shared-types';
import { cn, localizedDateFormatter } from '@anytag/shared-utils';
import { EngagementCampaignPostStatus, useEngagementDraftPostHistoryQuery } from '@anytag/graphql';
import { PostModal, usePostModalContext } from '../shared';

export interface HistoryTemplateProps {
  className?: string;
}

export const HistoryTemplate = ({ className }: HistoryTemplateProps) => {
  const { t, i18n } = useTranslation();
  const { selectedPostId } = usePostModalContext();
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const { data, loading } = useEngagementDraftPostHistoryQuery({
    skip: !selectedPostId,
    fetchPolicy: 'cache-and-network',
    variables: { pk: Number(selectedPostId) },
    onCompleted: ({ engagementDraftPostHistory }) => {
      setSelectedHistoryId(engagementDraftPostHistory?.historyRecords?.[0]?.id || null);
    }
  });

  const historyData = data?.engagementDraftPostHistory;
  const historyRecords = historyData?.historyRecords || [];
  const influencerInfo = historyData?.engagementPostDetailInfluencer;
  const selectedPostPreviewData = historyRecords.find((record) => record.id === selectedHistoryId);

  return (
    <PostModal.HistoryTemplate loading={loading} className={className} data={historyRecords}>
      {(data) => (
        <div className="flex min-h-[inherit] flex-1 gap-x-4">
          <div className="relative w-(--image-thumbnail-width) flex-none overflow-hidden border-r-border">
            {/* top:0,bottom:0,left:0,right:0 -> trick to make the div take the full width/height of the parent and can be scrolled without using height or max-height. So the post body size won't affect to this behavior */}
            <div className="absolute top-0 right-0 bottom-0 left-0 overflow-y-auto">
              {data.map((post, i) => {
                const isRejected = post.reason === 'You rejected the video'; // currently only one case then we need to change UI

                return (
                  <div
                    key={`${post.id}_${i}`}
                    onClick={() => (post.id ? setSelectedHistoryId(post.id) : null)}
                    className={cn('box-border w-full border-b-border p-2', {
                      'bg-black-20': post.id === selectedHistoryId,
                      'pointer-events-none': !post.id
                    })}
                  >
                    <div className="flex-none text-sm text-black-60">
                      {localizedDateFormatter(post.createdDate, 'PPP', i18n.language as AppLanguage)}
                    </div>
                    <div className="mt-1 flex-1 text-base font-semibold">
                      {post.postStatus === EngagementCampaignPostStatus.READY_TO_POST ? (
                        <span className="text-dark-green-70">{t('Approved the post')}</span>
                      ) : (
                        <>
                          <p className={cn({ 'text-error': isRejected })}>{t(post.reason || '')}</p>
                          <p className="text-sm break-all text-black-60">{post.detailedReason}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedHistoryId ? (
            <div className="relative box-border flex flex-1 flex-col">
              {/* top:0,bottom:0,left:0,right:0 -> trick to make the div take the full width/height of the parent and can be scrolled without using height or max-height. So the post body size won't affect to this behavior */}
              <div className="absolute top-0 right-0 bottom-0 left-0 box-border flex h-full flex-col gap-4 overflow-hidden">
                {influencerInfo ? (
                  <div className="flex flex-none justify-between gap-1">
                    <PostModal.InfluencerInfo
                      className="flex-1"
                      info={{
                        id: String(influencerInfo?.id ?? ''),
                        name: influencerInfo?.name,
                        avatar: influencerInfo?.avatar,
                        followers: influencerInfo?.followersCount
                      }}
                    />
                    <PostModal.Date value={selectedPostPreviewData?.createdDate} />
                  </div>
                ) : null}

                <div className="flex flex-1 gap-2 overflow-hidden">
                  {selectedPostPreviewData?.images?.length || selectedPostPreviewData?.postContent ? (
                    <>
                      <PostModal.MediaPreview
                        images={selectedPostPreviewData?.images || []}
                        className={cn('size-full flex-1', { 'max-w-1/2': !!selectedPostPreviewData?.postContent })}
                      />
                      {selectedPostPreviewData?.postContent ? (
                        <PostModal.Caption
                          className={cn('flex-1', { 'max-w-1/2': !!selectedPostPreviewData?.images?.length })}
                          caption={selectedPostPreviewData?.postContent}
                        />
                      ) : null}
                    </>
                  ) : (
                    <div className="flex size-full items-center justify-center text-base text-black-60">
                      {t('No preview data available')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </PostModal.HistoryTemplate>
  );
};
